import { Public } from '../types';
import {
  HttpContentType,
  HttpFetchProps,
  HttpAdapter,
  JsonResponse
} from './types';

type HttpBodyInfo = {
  body: RequestInit['body'];
  contentType?: HttpContentType;
};

/** Public interface of @see HttpService. */
export type IHttpService = Public<HttpService>;

/** Service used to make HTTP requests. */
export class HttpService {
  private _adapters: HttpAdapter[];

  constructor(
    requestAdapters?: HttpAdapter[]
  ) {
    this._adapters = requestAdapters ?? [];
  }

  /**
   * Create a multipart form to send as the body of a request.
   *
   * @note Browser automatically sends `content-type: "multipart/form-data"` header.
   */
  createMultipartForm(data: Record<string, unknown>): FormData {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      const validValue = value instanceof Blob || typeof value === 'string';
      const formValue = (validValue) ? value : (
        new Blob([JSON.stringify(value)], { type: 'application/json' })
      );
      formData.append(key, formValue);
    });

    return formData;
  }

  /**
   * Create a URL form to send as the body of a request.
   *
   * @note Browser automatically sends `content-type: "application/x-www-form-urlencoded"` header.
   */
  createUrlForm(data: Record<string, string>): URLSearchParams {
    return new URLSearchParams(data);
  }

  /** Fetch a request. */
  async fetch(props: HttpFetchProps): Promise<Response> {
    const originalProps = props;
    for (const adapter of this._adapters) {
      props = (typeof adapter.adaptHttpRequest !== 'function') ? props : (
        await adapter.adaptHttpRequest(props)
      );
    }

    const {
      body,
      contentType
    } = this._getBodyInfo(props);
    props.headers ??= {};
    if (contentType) {
      props.headers['Content-Type'] ??= contentType;
    }

    let { query, url, ...requestInit } = props;
    const queryString = Object.entries(query ?? {}).map(([key, value]) => {
      return `${key}=${encodeURIComponent(value)}`;
    }).join('&');
    if (query) {
      const separator = (url.includes('?')) ? '&' : '?';
      url = `${url}${separator}${queryString}`;
    }
    let response = await fetch(url, {
      ...requestInit,
      body
    });
    for (const adapter of this._adapters) {
      response = (typeof adapter.adaptHttpResponse !== 'function') ? response : (
        await adapter.adaptHttpResponse({
          request: originalProps,
          response,
          service: this
        })
      );
    }
    return response;
  }

  /** Fetch a request and return parsed JSON. */
  async fetchJson<T>(props: HttpFetchProps): Promise<JsonResponse<T>> {
    try {
      const response = await this.fetch(props);
      if (!response.ok) {
        return { error: response };
      }

      return { value: await response.json() };
    } catch (error) {
      if (error instanceof Error) {
        return { error };
      } else {
        // This should not happen.
        throw error;
      }
    }
  }

  /** Fetch a request and return parsed JSON or throw an error. */
  async fetchJsonOrFail<T>(props: HttpFetchProps): Promise<T> {
    const { error, value } = await this.fetchJson<T>(props);
    if (error) {
      throw error;
    }
    return value;
  }

  /** Get information about the body to add to the request. */
  private _getBodyInfo({
    body
  }: HttpFetchProps): HttpBodyInfo {
    if (
      body == null
      || typeof body === 'string'
      || body instanceof FormData
      || body instanceof URLSearchParams
    ) {
      // Let content type get set automatically.
      return { body };
    }

    return {
      body: JSON.stringify(body),
      contentType: HttpContentType.applicationJson
    };
  }
}
